import AssignmentRoundedIcon from '@material-ui/icons/AssignmentRounded';
import CloseIcon from '@material-ui/icons/Close';
import { Link } from '@reach/router';
import React from 'react';
import styled, { css, keyframes } from 'styled-components/macro';
import {
  focusRing,
  fontSize,
  fontWeight,
  layout,
  lineHeight,
  media,
  neutral,
  radius,
  shadow,
  space,
  transition,
} from '../../../theme/tokens';

export const StudentHeaderRow = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${space[4]};
  padding: ${space[5]} ${layout.gutter};

  ${media.md`
    padding: ${space[7]} ${layout.gutterWide} ${space[5]};
  `}
`;

export const StudentHeader = styled.h1`
  margin: 0;
  font-weight: ${fontWeight.heavy};
  font-size: ${fontSize.xxl};
  line-height: ${lineHeight.tight};
  color: ${({ theme }) => theme.headerColour};
  overflow-wrap: anywhere;
`;

export const StudentImage = styled.img`
  flex-shrink: 0;
  height: 6.4rem;
  width: 6.4rem;
  border-radius: ${radius.md};
  object-fit: cover;
  box-shadow: ${shadow.sm};

  ${media.md`
    height: 8rem;
    width: 8rem;
  `}
`;

export const NavRow = styled.nav`
  display: flex;
  justify-content: center;
  gap: ${space[2]};
  margin: 0 ${layout.gutter} ${space[4]};
  padding: ${space[1]};
  border-radius: ${radius.pill};
  background: ${neutral.subtle};

  ${media.sm`
    width: fit-content;
    margin: 0 auto ${space[5]};
  `}
`;

export const EvidenceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space[4]};
  width: 100%;
  max-width: ${layout.contentMaxWidth};
  margin: 0 auto;
  /* clears the fixed 8rem MessageForm, plus a gap so the last item doesn't sit against it */
  padding: 0 ${layout.gutter} calc(8rem + ${space[6]});

  ${media.md`
    padding: 0 ${layout.gutterWide} calc(8rem + ${space[6]});
  `}
`;

export const StudentItemList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
  max-width: ${layout.readableMaxWidth};
  margin: 0 auto;
  padding: 0 ${layout.gutter} ${space[7]};
  list-style-type: none;
`;

export const ItemLi = styled.li``;

export const UnStyledLink = styled(Link)`
  color: inherit;
  text-decoration: none;
`;

export const SpaceBetweenLink = styled(UnStyledLink)`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${space[3]};
  border-radius: ${radius.lg};
  ${focusRing}
`;

export const CenterLink = styled(UnStyledLink)`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${space[4]};
`;

export const NavLink = styled(UnStyledLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: ${layout.touchTarget};
  padding: 0 ${space[5]};
  border-radius: ${radius.pill};
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.lg};
  color: ${({ theme }) => theme.headerColour};
  transition: background ${transition.fast}, color ${transition.fast};

  &[data-ispartiallycurrent='true'] {
    background: ${({ theme }) => theme.lessonHighlight};
    color: ${({ theme }) =>
      theme.isLessonDark ? theme.textLight : theme.headerColour};
    box-shadow: ${shadow.sm};
  }
  ${focusRing}
`;

export const ActiveLink = (props) => {
  const getProps = ({ isPartiallyCurrent, isCurrent }) => {
    return (isPartiallyCurrent && props.to !== '/') || isCurrent
      ? { 'data-ispartiallycurrent': true }
      : {};
  };
  return <NavLink getProps={getProps} {...props} />;
};

export const ItemInstructions = styled.p`
  width: 100%;
  margin: ${space[4]} 0;
  padding: ${space[4]};
  border-radius: ${radius.lg};
  font-weight: ${fontWeight.semibold};
  font-size: ${fontSize.lg};
  line-height: ${lineHeight.normal};
  color: ${({ theme, complete }) =>
    complete ? theme.textLight : theme.headerColour};
  background: ${({ theme, complete }) =>
    complete ? theme.lessonHighlight : neutral.subtle};
  white-space: pre-line;
  overflow-wrap: anywhere;

  ${({ hasNotifications }) =>
    hasNotifications &&
    css`
      margin: 0;
    `}
`;

export const NoItemsLi = styled.li`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const CompleteNote = styled.p`
  display: flex;
  align-items: center;
  gap: ${space[2]};
  width: 100%;
  margin: 0;
  padding: ${space[3]} ${space[4]};
  border: 1px solid ${neutral.border};
  border-radius: ${radius.md};
  background: ${neutral.canvas};
  font-size: ${fontSize.md};
  line-height: ${lineHeight.normal};
  color: ${neutral.textMuted};
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${fadeIn} 1s linear;
  min-height: calc(100vh - max(17vh, 134px));

  ${media.md`
    min-height: 100vh;
  `}
`;

export const XIcon = (props) => {
  return <CloseIcon {...props} />;
};

export const AssessmentIcon = (props) => {
  return <AssignmentRoundedIcon style={{ fontSize: '5.0rem' }} {...props} />;
};

export const EvidenceButton = styled.button`
  grid-area: evidence;
  background: none;
  border: none;
  margin: 0;

  &:hover {
    cursor: pointer;
  }

  &:disabled {
    cursor: default;
  }
`;
